/**
 * 좌측 메뉴 구성
 * ---------------------------------------------------------------------------
 * 메뉴를 한곳에 선언으로 모은다. 예전에는 App.tsx 안에서 배열을 이어 붙였고,
 * 화면이 늘 때마다 순서가 밀려 성격이 다른 것들이 섞였다 — 「운영 현황」과
 * 「구성원」이 나란히 있고 「과금 관리」는 저 아래에 있는 식이었다.
 *
 * 규칙
 *   1. 비슷한 것끼리 묶는다. 묶음은 접힌 메뉴(SubMenu)가 된다.
 *   2. 매일 여는 첫 화면은 묶지 않고 맨 위에 둔다. 한 번 더 펼치게 하지 않는다.
 *   3. 화면을 더할 때는 이 표에 한 줄을 넣는다. 그 줄의 `scope` 가 누구에게
 *      보일지를 정하고, App.tsx 의 라우트와 `key` 로 짝을 맞춘다.
 *
 * scope
 *   TENANT    기업 관리자에게 보인다. 통합관리자에게는 「내 기업」 묶음 안에
 *             같은 화면이 들어간다 — 운영자도 자기 회사 구성원을 관리한다.
 *   PLATFORM  통합관리자에게만. 지원 모드 중에는 숨긴다(남의 회사에 들어가
 *             있는 상태에서 전체 관리 메뉴가 같이 보이면 헷갈린다).
 *   BOTH      누구에게나.
 *
 * permission
 *   그 화면의 API 가 요구하는 권한. <b>없으면 메뉴에서 뺀다.</b>
 *
 *   예전에는 scope 만 보고 기업 화면을 전부 띄웠다. 그래서 일반 구성원
 *   (MEMBER)에게도 구성원·역할·청구·로그가 그대로 보였고, 누르면 403 이
 *   떴다. 2026-10-26 점검에서 세어 보니 <b>여덟 화면 중 일곱이 403</b>
 *   이었다 — 로그인 직후 떨어지는 첫 화면(자사 현황)까지 포함해서다.
 *   막는 일은 서버가 이미 하고 있으니 보안 구멍은 아니지만, 쓰는 사람에게는
 *   "눌러 봐야 아는 메뉴" 가 된다.
 *
 *   값은 서버의 `@RequirePermission` 과 같아야 한다. 어긋나면 메뉴는
 *   보이는데 화면이 403 이거나(느슨) 쓸 수 있는 화면이 숨는다(빡빡).
 */

import type { ReactNode } from 'react';
// 아이콘은 묶음에만 단다. 두 단계 모두 달면 눈이 어디를 따라가야 할지 모른다.
import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  ControlOutlined,
  DashboardOutlined,
  DollarOutlined,
  FileSearchOutlined,
  MonitorOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';

export type MenuScope = 'TENANT' | 'PLATFORM' | 'BOTH';

export interface MenuEntry {
  /** 경로의 /app 다음 한 칸. 라우트와 같아야 한다. */
  key: string;
  label: string;
  icon?: ReactNode;
  scope: MenuScope;
  /** 이 화면을 여는 데 필요한 권한. 비우면 누구에게나 보인다. */
  permission?: string;
}

export interface MenuGroup {
  /** 묶음 식별자. 경로가 아니므로 'g:' 를 붙여 화면 키와 섞이지 않게 한다. */
  key: string;
  label: string;
  icon?: ReactNode;
  scope: MenuScope;
  children: MenuEntry[];
}

type Node = MenuEntry | MenuGroup;

const isGroup = (node: Node): node is MenuGroup => 'children' in node;

/* =========================================================================
 * 선언
 * ====================================================================== */

/**
 * 기업의 첫 화면.
 *
 * 권한을 걸지 않는다. 이 화면은 누구나 열고, 안에서 볼 수 없는 칸만
 * 빠진다(서버의 TenantDashboardService 가 그렇게 내려 준다). 메뉴에서
 * 숨기면 일반 구성원에게 돌아갈 첫 화면이 없어진다.
 */
const TENANT_HOME: MenuEntry = {
  key: 'dashboard',
  label: '자사 현황',
  icon: <DashboardOutlined />,
  scope: 'TENANT',
};

/** 통합관리자의 첫 화면. */
const PLATFORM_HOME: MenuEntry = {
  key: 'overview',
  label: '운영 현황',
  icon: <DashboardOutlined />,
  scope: 'PLATFORM',
};

/**
 * 서버 상태. 「운영 현황」 바로 아래, 묶음 밖에 둔다.
 *
 * 규칙 2(매일 여는 화면은 묶지 않는다)에 해당한다. 원래는 「시스템」 묶음
 * 안에 있었는데, 거기는 공통코드·메일 설정처럼 **한 번 정해 두고 잘 안 여는**
 * 것들이 모인 자리다. CPU·메모리·디스크는 이상이 의심될 때 바로 열어 봐야
 * 하므로 한 번 더 펼치게 하지 않는다.
 *
 * 「시스템」 묶음에 같은 줄을 남겨 두지 않았다. antd Menu 는 key 로 선택 상태를
 * 가리므로, 같은 key 가 두 자리에 있으면 두 곳이 함께 켜지고 묶음도 같이
 * 펼쳐진다.
 */
const PLATFORM_STATUS: MenuEntry = {
  key: 'system',
  label: '시스템 상태',
  icon: <MonitorOutlined />,
  scope: 'PLATFORM',
};

/**
 * 기업(자사) 화면들.
 *
 * 통합관리자에게는 이 묶음들이 「내 기업」 하나로 다시 묶인다. 운영자의
 * 일은 전체 관리이고 자기 회사 관리는 가끔이라, 위쪽을 차지하면 안 된다.
 */
const TENANT_GROUPS: MenuGroup[] = [
  {
    key: 'g:org',
    label: '조직',
    icon: <TeamOutlined />,
    scope: 'TENANT',
    children: [
      { key: 'users', label: '구성원', scope: 'TENANT', permission: 'platform.user:read' },
      { key: 'departments', label: '조직도', scope: 'TENANT', permission: 'platform.dept:read' },
      { key: 'roles', label: '역할·권한', scope: 'TENANT', permission: 'platform.role:read' },
    ],
  },
  {
    key: 'g:subscription',
    label: '구독',
    icon: <AppstoreOutlined />,
    scope: 'TENANT',
    children: [
      { key: 'store', label: '제품', scope: 'TENANT', permission: 'platform.billing:read' },
      { key: 'billing', label: '사용량·청구', scope: 'TENANT', permission: 'platform.billing:read' },
    ],
  },
  {
    key: 'g:records',
    label: '기록',
    icon: <FileSearchOutlined />,
    scope: 'TENANT',
    children: [
      { key: 'logs', label: '로그 조회', scope: 'TENANT', permission: 'platform.audit:read' },
      /*
       * 만든 AI 보고서를 다시 보는 자리.
       *
       * 전에는 자사 현황의 [AI 주간 보고서] **팝업 안 탭**으로만 있어서,
       * 만든 적 없는 사람은 그런 것이 있는 줄도 몰랐다. 쌓인 것을 보는
       * 자리는 메뉴에 있어야 한다 — WORK&ECO 도 같은 모양이다.
       *
       * 권한은 `/ai-report` 가 요구하는 것과 같아야 한다(platform.tenant:read).
       * 메뉴만 열어 두면 눌렀을 때 403 이 난다.
       */
      { key: 'ai-reports', label: 'AI 보고서', scope: 'TENANT', permission: 'platform.tenant:read' },
    ],
  },
  {
    key: 'g:settings',
    label: '설정',
    icon: <SettingOutlined />,
    scope: 'TENANT',
    children: [
      { key: 'settings', label: '기업 설정', scope: 'TENANT', permission: 'platform.tenant:read' },
    ],
  },
];

/** 통합관리자 화면들. */
const PLATFORM_GROUPS: MenuGroup[] = [
  {
    key: 'g:customers',
    label: '고객',
    icon: <BankOutlined />,
    scope: 'PLATFORM',
    children: [
      { key: 'tenants', label: '기업 관리', scope: 'PLATFORM' },
      { key: 'products', label: '제품 현황', scope: 'PLATFORM' },
      /*
       * 기업별 사용량.
       *
       * 「시스템 상태」 맨 아래 카드로만 있었다. 거기는 CPU·메모리·디스크를
       * 보러 가는 자리라, **어느 고객이 한도에 가까운가** 를 보려고 매번
       * 서버 지표를 지나쳐 스크롤해야 했다. 고객을 보는 질문이므로
       * 「고객」 묶음에 둔다.
       */
      { key: 'tenant-usage', label: '기업별 사용량', scope: 'PLATFORM' },
    ],
  },
  {
    key: 'g:revenue',
    label: '과금',
    icon: <DollarOutlined />,
    scope: 'PLATFORM',
    children: [
      { key: 'admin-billing', label: '구독·청구', scope: 'PLATFORM' },
      /*
       * 「구독·청구」는 청구서 단위, 「수납 내역」은 입금 건 단위다.
       * 둘을 한 화면에 두면 "이 청구서가 완납인가" 와 "왜 카드가 거절됐나"
       * 라는 서로 다른 질문이 섞인다. 청구 바로 다음에 둔다 — 청구서를
       * 보다가 "그래서 실제로 들어왔나" 로 넘어가는 순서다.
       */
      { key: 'payments', label: '수납 내역', scope: 'PLATFORM' },
      { key: 'revenue', label: '매출 현황', scope: 'PLATFORM' },
      { key: 'plans', label: '요금제', scope: 'PLATFORM' },
    ],
  },
  {
    key: 'g:system',
    label: '시스템',
    icon: <ControlOutlined />,
    scope: 'PLATFORM',
    children: [
      { key: 'codes', label: '공통코드', scope: 'PLATFORM' },
      { key: 'ai', label: 'AI 설정', scope: 'PLATFORM' },
      { key: 'mail', label: '메일 설정', scope: 'PLATFORM' },
      { key: 'mail-templates', label: '메일 서식', scope: 'PLATFORM' },
      { key: 'integrations', label: '외부 연동', scope: 'PLATFORM' },
      { key: 'footer', label: '바닥글', scope: 'PLATFORM' },
      // 「시스템 상태」는 PLATFORM_STATUS 로 올렸다 (운영 현황 바로 아래).
      { key: 'admin-logs', label: '전체 로그', scope: 'PLATFORM' },
    ],
  },
];

/** 언제나 맨 아래. */
const ACCOUNT: MenuEntry = {
  key: 'account',
  label: '내 계정',
  icon: <UserOutlined />,
  scope: 'BOTH',
};

/* =========================================================================
 * 조립
 * ====================================================================== */

export interface MenuContext {
  isPlatformAdmin: boolean;
  /** 지원 모드 중인가. 남의 회사에 들어가 있는 상태다. */
  support: boolean;
  /**
   * 이 사용자가 가진 권한. `/auth/me` 의 permissions 를 그대로 넣는다.
   *
   * <b>undefined 면 거르지 않는다.</b> 아직 프로필을 못 받은 찰나에 메뉴가
   * 텅 비었다가 채워지면 깜빡인다. 빈 배열(권한이 정말 없음)과 구분해야
   * 하므로 `?? []` 로 뭉개지 않는다.
   */
  permissions?: string[];
}

/** 이 화면을 메뉴에 띄울 것인가. */
function allowed(entry: MenuEntry, ctx: MenuContext): boolean {
  if (!entry.permission) return true;
  if (!ctx.permissions) return true; // 아직 모름 — 숨기지 않는다
  // 운영자는 자기 회사 화면도 모두 연다(서버 가드도 그렇게 동작한다).
  if (ctx.isPlatformAdmin) return true;
  return ctx.permissions.includes(entry.permission);
}

/** 묶음에서 볼 수 있는 화면만 남긴다. 하나도 없으면 묶음째 뺀다. */
function prune(groups: MenuGroup[], ctx: MenuContext): MenuGroup[] {
  return groups
    .map((g) => ({ ...g, children: g.children.filter((c) => allowed(c, ctx)) }))
    .filter((g) => g.children.length > 0);
}

/**
 * antd Menu 가 먹는 모양.
 *
 * children 이 있으면 묶음(SubMenu), 없으면 화면이다. antd 의 타입은 그 둘을
 * 유니온으로 나누므로 여기서도 나눠 둔다 — 하나로 합치고 children 을 선택으로
 * 두면 대입이 되지 않는다.
 */
export type AntdMenuItem =
  | { key: string; label: string; icon?: ReactNode }
  | { key: string; label: string; icon?: ReactNode; children: AntdMenuItem[] };

/**
 * 메뉴 트리.
 *
 * 통합관리자는 전체 관리가 먼저고 자기 회사 화면은 「내 기업」 안으로
 * 들어간다. 지원 모드 중에는 통합관리자 메뉴를 감추고 기업 화면만 남긴다 —
 * 지금 보고 있는 것이 고객사 데이터이기 때문이다.
 */
export function buildMenu(ctx: MenuContext): AntdMenuItem[] {
  const asPlatform = ctx.isPlatformAdmin && !ctx.support;

  if (!asPlatform) {
    return [
      ...(allowed(TENANT_HOME, ctx) ? [toItem(TENANT_HOME)] : []),
      ...prune(TENANT_GROUPS, ctx).map(toItem),
      toItem(ACCOUNT),
    ];
  }

  return [
    toItem(PLATFORM_HOME),
    toItem(PLATFORM_STATUS),
    ...PLATFORM_GROUPS.map(toItem),
    {
      key: 'g:mine',
      label: '내 기업',
      icon: <AuditOutlined />,
      // 묶음 안의 묶음은 두지 않는다. 두 단계까지가 눈으로 따라갈 수 있는 한계다.
      children: prune(TENANT_GROUPS, ctx)
        .flatMap((g) => g.children)
        .map(toItem),
    },
    toItem(ACCOUNT),
  ];
}

function toItem(node: Node): AntdMenuItem {
  return isGroup(node)
    ? { key: node.key, label: node.label, icon: node.icon, children: node.children.map(toItem) }
    : { key: node.key, label: node.label, icon: node.icon };
}

/**
 * 지금 화면이 속한 묶음.
 *
 * 새로고침하거나 링크로 바로 들어와도 그 묶음이 펼쳐져 있어야 한다.
 * 접힌 채로 두면 사용자가 자기 위치를 잃는다.
 */
export function openKeysFor(selected: string, ctx: MenuContext): string[] {
  const asPlatform = ctx.isPlatformAdmin && !ctx.support;
  const tenantGroups = prune(TENANT_GROUPS, ctx);
  const groups: MenuGroup[] = asPlatform
    ? [
        ...PLATFORM_GROUPS,
        {
          key: 'g:mine',
          label: '내 기업',
          scope: 'PLATFORM',
          children: tenantGroups.flatMap((g) => g.children),
        },
      ]
    : tenantGroups;

  return groups.filter((g) => g.children.some((c) => c.key === selected)).map((g) => g.key);
}

/**
 * 이 사용자의 첫 화면. 로그인 직후와 잘못된 경로가 모두 여기로 온다.
 *
 * 「자사 현황」은 platform.tenant:read 가 있어야 열린다. 일반 구성원에게는
 * 그 권한이 없어서, 예전에는 <b>로그인하자마자 403 화면</b>을 봤다. 그
 * 사람이 실제로 열 수 있는 첫 메뉴로 보낸다. 하나도 없으면 「내 계정」이다 —
 * 그 화면은 누구에게나 열려 있다.
 */
export function homeKeyFor(ctx: MenuContext): string {
  if (ctx.isPlatformAdmin && !ctx.support) return PLATFORM_HOME.key;
  if (allowed(TENANT_HOME, ctx)) return TENANT_HOME.key;

  const first = prune(TENANT_GROUPS, ctx)[0]?.children[0]?.key;
  return first ?? ACCOUNT.key;
}

/* =========================================================================
 * 검색 (Ctrl+K)
 * ====================================================================== */

export interface SearchableMenuItem {
  /** 경로의 /app 다음 한 칸. 그대로 이동에 쓴다. */
  key: string;
  label: string;
  /** 「조직 › 구성원」처럼 어디에 있는 화면인지. 같은 이름이 둘일 때 구분된다. */
  trail: string;
  icon?: ReactNode;
}

/**
 * 메뉴 검색이 뒤질 목록.
 *
 * 선언(TENANT_GROUPS 등)을 다시 읽지 않고 **buildMenu 가 만든 결과를 펼친다.**
 * 선언을 따로 읽으면 "메뉴에는 안 보이는데 검색에는 나오는 화면" 이 생긴다 —
 * 통합관리자/지원 모드에 따라 보이는 것이 달라지는데, 그 판단이 buildMenu
 * 안에만 있기 때문이다. 같은 입력에서 뽑으면 어긋날 수가 없다.
 *
 * 묶음 자체(g: 로 시작하는 것)는 넣지 않는다. 열 수 있는 화면이 아니다.
 */
export function searchableMenu(ctx: MenuContext): SearchableMenuItem[] {
  const out: SearchableMenuItem[] = [];

  const walk = (items: AntdMenuItem[], trail: string[]) => {
    for (const item of items) {
      if ('children' in item) {
        walk(item.children, [...trail, item.label]);
      } else {
        out.push({
          key: item.key,
          label: item.label,
          trail: trail.join(' › '),
          icon: item.icon,
        });
      }
    }
  };

  walk(buildMenu(ctx), []);
  return out;
}

/**
 * 검색어와 얼마나 맞는가. 작을수록 앞에 온다. 안 맞으면 -1.
 *
 * 이름이 먼저고 묶음 이름은 그다음이다 — 「조직」을 치면 조직도가
 * 조직 묶음 안의 다른 화면들보다 위에 와야 한다.
 */
export function scoreMenuMatch(item: SearchableMenuItem, keyword: string): number {
  const q = keyword.trim().toLowerCase();
  if (!q) return 0;

  const label = item.label.toLowerCase();
  const trail = item.trail.toLowerCase();

  if (label === q) return 0;
  if (label.startsWith(q)) return 1;
  if (label.includes(q)) return 2;
  if (trail.includes(q)) return 3;
  if (item.key.toLowerCase().includes(q)) return 4;
  return -1;
}

/** 아이콘을 쓰지 않는 자리(기업 설정 화면 안내 등)를 위해 이름만 뽑는다. */
export function labelOf(key: string): string | null {
  const all: Node[] = [
    TENANT_HOME,
    PLATFORM_HOME,
    PLATFORM_STATUS,
    ACCOUNT,
    ...TENANT_GROUPS,
    ...PLATFORM_GROUPS,
  ];
  for (const node of all) {
    if (node.key === key) return node.label;
    if (isGroup(node)) {
      const child = node.children.find((c) => c.key === key);
      if (child) return child.label;
    }
  }
  return null;
}
